'use client';

import { c as _c } from "react/compiler-runtime";
import { isServer, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryStreamedHydration } from '@tanstack/react-query-next-experimental';
const DEFAULT_RETRY_COUNT = 3;

// Treat a thrown value as a "client error" only when it carries an HTTP 4xx
// status. Anything without a numeric status (e.g., network failures, plain
// Error throws) keeps the default retry behavior.
function isClientError(error: unknown): boolean {
  const status = (error as {
    status?: unknown;
  } | null)?.status;
  return typeof status === 'number' && status >= 400 && status < 500;
}
function makeQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 60 * 1000,
        // 4xx responses won't change on retry — skip them. Reduces Sentry noise
        // for routes that poll endpoints which legitimately 404 (deleted/stale
        // resources). See BRIDGE-DASHBOARD-1GM.
        // queryFns read useServerState(), valid only in render; a server retry runs outside it.
        retry: isServer ? false : (failureCount, error) => {
          if (isClientError(error)) return false;
          return failureCount < DEFAULT_RETRY_COUNT;
        }
      }
    }
  });
}
let browserQueryClient: QueryClient | undefined = undefined;
export function getQueryClient() {
  if (isServer) {
    // Server: always make a new query client
    return makeQueryClient();
  } else {
    // Browser: make a new query client if we don't already have one
    // This is very important, so we don't re-make a new client if React
    // suspends during the initial render. This may not be needed if we
    // have a suspense boundary BELOW the creation of the query client
    if (!browserQueryClient) browserQueryClient = makeQueryClient();
    return browserQueryClient;
  }
}
export default function QueryProvider(props) {
  const $ = _c(3);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = getQueryClient();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const queryClient = t0;
  let t1;
  if ($[1] !== props.children) {
    t1 = <QueryClientProvider client={queryClient}><ReactQueryStreamedHydration>{props.children}</ReactQueryStreamedHydration></QueryClientProvider>;
    $[1] = props.children;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}